import { PaletteOptions } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { tokens } from './tokens';
import '../variants';

/**
 * MUI adapter for the Studio Vault colour system.
 *
 * Keep semantic colour decisions in `tokens.ts`; this file only maps those
 * roles onto MUI. That prevents MUI surfaces and custom product surfaces from
 * drifting into separate black/grey/orange palettes.
 */
export const createPalette = (mode: 'light' | 'dark'): PaletteOptions => {
  const t = tokens(mode);
  const isDark = mode === 'dark';

  return {
    studio: t,
    mode,
    primary: {
      main: t.colors.accentFill,
      light: isDark ? t.colors.accentText : '#FF7A33',
      dark: isDark ? '#D94A05' : '#B83E04',
      contrastText: t.colors.onAccent,
    },
    secondary: {
      main: t.colors.cyan,
      light: t.colors.cyanLight,
      dark: isDark ? '#3297B5' : '#176D87',
      contrastText: t.colors.onCyan,
    },
    error: { main: t.colors.red },
    warning: { main: t.colors.amber, contrastText: t.colors.onAccent },
    info: { main: t.colors.cyan, contrastText: t.colors.onCyan },
    success: { main: t.colors.green, contrastText: t.colors.onGreen },
    grey: isDark
      ? {
          50: t.text.primary,
          100: t.text.bright,
          200: '#CDCAC6',
          300: t.text.secondary,
          400: '#A29E98',
          500: t.text.muted,
          600: t.text.faint,
          700: t.surfaces.iconWell,
          800: t.surfaces.panel,
          900: t.surfaces.screen,
        }
      : {
          50: '#FBFAF8',
          100: '#F6F5F2',
          200: '#EDEBE6',
          300: '#D7D5D0',
          400: '#B0B4BA',
          500: '#8B9098',
          600: '#6D727B',
          700: '#5A5F68',
          800: '#2C3038',
          900: '#14161A',
        },
    background: {
      default: t.surfaces.canvas,
      paper: t.surfaces.panel,
    },
    DataGrid: {
      bg: 'transparent',
      headerBg: 'transparent',
      pinnedBg: t.surfaces.canvas,
    },
    text: {
      primary: t.text.primary,
      secondary: t.text.secondary,
      disabled: t.text.faint,
    },
    divider: t.borders.divider,
    action: {
      hover: t.surfaces.fillHover,
      selected: t.surfaces.fillActive,
      disabled: t.text.faint,
      disabledBackground: t.surfaces.fillWeak,
      focus: t.surfaces.fillActive,
    },
  };
};

export const palette = createPalette('dark');
